import { useState, useEffect, useCallback } from "react";
import { API, AUTH, PASS_LABELS, fmtMoney, currentMonth, exportXLSX } from "../../adminUtils";
import ExportBar from "./ExportBar";

export default function MonthlyRevenueReport() {
  const [month, setMonth]     = useState(currentMonth());
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);

  const fetch_ = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${API}/reports/monthly-revenue?month=${month}`, { headers: AUTH });
      const json = await res.json();
      setData(json);
    } catch { setData(null); }
    finally   { setLoading(false); }
  }, [month]);

  useEffect(() => { fetch_(); }, [fetch_]);

  const doExport = () => {
    if (!data) return;
    const rows = [];
    rows.push({ Category: "Drop-in Classes", Type: "Drop-in", Count: data.dropins.count, Total: data.dropins.total });
    for (const [type, d] of Object.entries(data.passes ?? {})) {
      rows.push({ Category: "Pass Sales", Type: PASS_LABELS[type] ?? type, Count: d.count, Total: d.total });
    }
    for (const [type, d] of Object.entries(data.memberships?.collected ?? {})) {
      rows.push({ Category: "Memberships", Type: PASS_LABELS[type] ?? type, Count: d.count, Total: d.total, Notes: "Collected" });
    }
    for (const [type, d] of Object.entries(data.memberships?.projected ?? {})) {
      rows.push({ Category: "Memberships", Type: PASS_LABELS[type] ?? type, Count: d.count, Total: d.total, Notes: "Projected" });
    }
    rows.push({ Category: "TOTAL COLLECTED", Total: data.totals.collected });
    rows.push({ Category: "PROJECTED",        Total: data.totals.projected });
    rows.push({ Category: "FORECAST",         Total: data.totals.forecast });
    exportXLSX("Monthly Revenue", rows, `iy-sp-revenue-${month}.xlsx`);
  };

  return (
    <div>
      <div className="adm-report-filters">
        <div className="adm-filter-group">
          <label className="adm-filter-label">Month</label>
          <input type="month" className="adm-input" value={month} onChange={e => setMonth(e.target.value)} />
        </div>
      </div>
      {loading && <p className="adm-loading">Loading…</p>}
      {data && !loading && (
        <>
          <div className="adm-report-summary-row">
            <div className="adm-summary-chip adm-summary-chip--green">
              <span className="adm-summary-val">{fmtMoney(data.totals.collected)}</span>
              <span className="adm-summary-lbl">Collected</span>
            </div>
            <div className="adm-summary-chip adm-summary-chip--amber">
              <span className="adm-summary-val">{fmtMoney(data.totals.projected)}</span>
              <span className="adm-summary-lbl">Projected</span>
            </div>
            <div className="adm-summary-chip adm-summary-chip--blue">
              <span className="adm-summary-val">{fmtMoney(data.totals.forecast)}</span>
              <span className="adm-summary-lbl">Month forecast</span>
            </div>
          </div>
          <ExportBar onXLSX={doExport} />

          <h4 className="adm-report-section-title">Drop-in Classes</h4>
          <table className="adm-report-table">
            <thead><tr><th>Type</th><th>Count</th><th>Total</th></tr></thead>
            <tbody>
              <tr><td>Drop-in classes</td><td>{data.dropins.count}</td><td>{fmtMoney(data.dropins.total)}</td></tr>
            </tbody>
          </table>

          <h4 className="adm-report-section-title">Pass Sales</h4>
          <table className="adm-report-table">
            <thead><tr><th>Pass Type</th><th>Sold</th><th>Revenue</th></tr></thead>
            <tbody>
              {Object.entries(data.passes ?? {}).length === 0
                ? <tr><td colSpan={3} className="adm-empty">No passes sold.</td></tr>
                : Object.entries(data.passes).map(([type, d]) => (
                    <tr key={type}><td>{PASS_LABELS[type] ?? type}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td></tr>
                  ))
              }
            </tbody>
          </table>

          <h4 className="adm-report-section-title">Memberships — Collected</h4>
          <table className="adm-report-table">
            <thead><tr><th>Type</th><th>Count</th><th>Revenue</th></tr></thead>
            <tbody>
              {Object.entries(data.memberships?.collected ?? {}).length === 0
                ? <tr><td colSpan={3} className="adm-empty">No memberships collected.</td></tr>
                : Object.entries(data.memberships.collected).map(([type, d]) => (
                    <tr key={type}><td>{PASS_LABELS[type] ?? type}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td></tr>
                  ))
              }
            </tbody>
          </table>

          {Object.keys(data.memberships?.projected ?? {}).length > 0 && (
            <>
              <h4 className="adm-report-section-title">Memberships — Projected</h4>
              <table className="adm-report-table">
                <thead><tr><th>Type</th><th>Count</th><th>Expected</th></tr></thead>
                <tbody>
                  {Object.entries(data.memberships.projected).map(([type, d]) => (
                    <tr key={type} className="adm-projected-row">
                      <td>{PASS_LABELS[type] ?? type}</td><td>{d.count}</td><td>{fmtMoney(d.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          <table className="adm-report-table adm-totals-table">
            <tbody>
              <tr className="adm-report-subtotal"><td>Total collected</td><td></td><td>{fmtMoney(data.totals.collected)}</td></tr>
              <tr className="adm-projected-row"><td>Projected (pending)</td><td></td><td>{fmtMoney(data.totals.projected)}</td></tr>
              <tr className="adm-forecast-row"><td>Month forecast</td><td></td><td>{fmtMoney(data.totals.forecast)}</td></tr>
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}